/**
 * Admin Super-Powers — "Create business" modal (super_admin, `b2b_accounts:create`).
 *
 * Provisions a B2B actor (venue / trainer / club / organizer) on a user's
 * behalf. The `primaryOwnerUserId` is the detail page's user — NOT a form field.
 * Composes Modal + FormField + Select + TextInput. Client-side validation via
 * the shared `AdminCreateActorInputSchema` from @bravemove/types.
 *
 * Two phases:
 *   1. The create form. On success it flips to →
 *   2. The success state: shows the created account (id + kind + the
 *      "Organizer auto-approved" / "Trainer profile created" notes when those
 *      flags are true).
 *
 * 409 ACCOUNT_ALREADY_EXISTS (MVP 1:1 — the user already owns an account) is
 * surfaced inline with the existing account id (read off the error body). 404
 * USER_NOT_FOUND + everything else fall through to a generic error.
 *
 * Gated by `b2b_accounts:create` — the parent detail page hides the trigger
 * button for any actor lacking the permission.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Select, TextInput } from '@bravemove/ui-web';
import {
  ACCOUNT_KIND_VALUES,
  AdminCreateActorInputSchema,
  type AccountKind,
  type AdminCreateActorInput,
} from '@bravemove/types';
import { ApiError } from '../../auth/types';
import { translateApiError } from '../../auth/errors.js';
import type { AdminCreateActorResult } from '../../api/b2b-account';

export interface CreateActorDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** The detail page's user — becomes `primaryOwnerUserId` (not a form field). */
  readonly ownerUserId: string;
  /** Wired to `adminB2bAccountApi.createAccount` by the parent. */
  readonly onCreate: (input: AdminCreateActorInput) => Promise<AdminCreateActorResult>;
  /** Called after success so the parent can refetch the user (new roles). */
  readonly onCreated?: (result: AdminCreateActorResult) => void;
}

const KIND_FALLBACK: Record<AccountKind, string> = {
  venue: 'Venue',
  trainer: 'Trainer',
  club: 'Club',
  organizer: 'Organizer',
};

export function CreateActorDialog({
  open,
  onClose,
  ownerUserId,
  onCreate,
  onCreated,
}: CreateActorDialogProps) {
  const { t } = useI18n();

  const [accountKind, setAccountKind] = useState<AccountKind>('venue');
  const [legalBusinessName, setLegalBusinessName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [countryCode, setCountryCode] = useState('LB');
  const [taxId, setTaxId] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [existingAccountId, setExistingAccountId] = useState<string | null>(null);
  const [created, setCreated] = useState<AdminCreateActorResult | null>(null);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    setAccountKind('venue');
    setLegalBusinessName('');
    setDisplayName('');
    setCountryCode('LB');
    setTaxId('');
    setSubmitting(false);
    setServerError(null);
    setExistingAccountId(null);
    setCreated(null);
  }, [open]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const validation = useMemo(() => {
    const candidate = {
      primaryOwnerUserId: ownerUserId,
      accountKind,
      legalBusinessName: legalBusinessName.trim(),
      displayName: displayName.trim(),
      countryCode: countryCode.trim() === '' ? 'LB' : countryCode.trim().toUpperCase(),
      taxId: taxId.trim() === '' ? undefined : taxId.trim(),
    };
    return AdminCreateActorInputSchema.safeParse(candidate);
  }, [ownerUserId, accountKind, legalBusinessName, displayName, countryCode, taxId]);

  const disableSubmit = submitting || !validation.success;

  const submit = async () => {
    if (!validation.success) return;
    setServerError(null);
    setExistingAccountId(null);
    setSubmitting(true);
    try {
      const result = await onCreate(validation.data);
      setCreated(result);
      onCreated?.(result);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ACCOUNT_ALREADY_EXISTS') {
        // The existing account rides on the error body (MVP 1:1).
        const existing = (err.body?.error as { existingAccount?: { id?: string } } | undefined)
          ?.existingAccount;
        setExistingAccountId(existing?.id ?? null);
        setServerError(
          t('admin:f43fc.createActor.error_already_exists', {
            defaultValue: 'This user already owns a business account.',
          }),
        );
      } else {
        setServerError(translateApiError(err, t));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Phase 2 — success view ────────────────────────────────────────────────
  if (created) {
    return (
      <Modal
        open={open}
        onClose={onClose}
        title={t('admin:f43fc.createActor.success_title', { defaultValue: 'Business created' })}
        size="md"
        footer={
          <Button variant="primary" onClick={onClose} data-testid="create-actor-done">
            {t('admin:f43fc.createActor.done', { defaultValue: 'Done' })}
          </Button>
        }
      >
        <div className="flex flex-col gap-3" data-testid="create-actor-success">
          <Banner tone="success">
            {t('admin:f43fc.createActor.success_body', {
              defaultValue: 'Created {kind} account for this user.',
              kind: t(`admin:f43fc.createActor.kind_${accountKind}`, {
                defaultValue: KIND_FALLBACK[accountKind],
              }),
            })}
          </Banner>
          <dl className="grid gap-2 text-[13px]">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-ink-3">
                {t('admin:f43fc.createActor.account_id', { defaultValue: 'Account ID' })}
              </dt>
              <dd className="font-mono text-[12px] text-ink" data-testid="create-actor-account-id">
                {created.account.id}
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-ink-3">
                {t('admin:f43fc.createActor.kind_label', { defaultValue: 'Kind' })}
              </dt>
              <dd className="text-ink">{KIND_FALLBACK[accountKind]}</dd>
            </div>
          </dl>
          {created.trainerProfileCreated ? (
            <div data-testid="create-actor-trainer-note">
              <Banner tone="info">
                {t('admin:f43fc.createActor.trainer_profile_created', {
                  defaultValue: 'A trainer profile was created for this user.',
                })}
              </Banner>
            </div>
          ) : null}
          {created.organizerApproved ? (
            <div data-testid="create-actor-organizer-note">
              <Banner tone="info">
                {t('admin:f43fc.createActor.organizer_approved', {
                  defaultValue: 'The organizer was auto-approved.',
                })}
              </Banner>
            </div>
          ) : null}
        </div>
      </Modal>
    );
  }

  // ── Phase 1 — the create form ─────────────────────────────────────────────
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:f43fc.createActor.title', { defaultValue: 'Create business' })}
      description={t('admin:f43fc.createActor.description', {
        defaultValue:
          'Provisions a business account on this user’s behalf and grants the matching role. A user can own one business account.',
      })}
      size="md"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="create-actor-cancel"
          >
            {t('admin:common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={disableSubmit}
            data-testid="create-actor-submit"
          >
            {t('admin:f43fc.createActor.submit', { defaultValue: 'Create business' })}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        data-testid="create-actor-dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <FormField
          label={t('admin:f43fc.createActor.kind_label', { defaultValue: 'Business type' })}
          required
        >
          <Select
            value={accountKind}
            onChange={(e) => setAccountKind(e.target.value as AccountKind)}
            options={ACCOUNT_KIND_VALUES.map((k) => ({
              value: k,
              label: t(`admin:f43fc.createActor.kind_${k}`, { defaultValue: KIND_FALLBACK[k] }),
            }))}
            data-testid="create-actor-kind"
            aria-label={t('admin:f43fc.createActor.kind_label', { defaultValue: 'Business type' })}
          />
        </FormField>

        <FormField
          label={t('admin:f43fc.createActor.legalName_label', {
            defaultValue: 'Legal business name',
          })}
          helper={t('admin:f43fc.createActor.legalName_helper', {
            defaultValue: '3–200 characters.',
          })}
          required
        >
          <TextInput
            value={legalBusinessName}
            onChange={(e) => setLegalBusinessName(e.target.value)}
            maxLength={200}
            data-testid="create-actor-legalName"
            aria-label={t('admin:f43fc.createActor.legalName_label', {
              defaultValue: 'Legal business name',
            })}
          />
        </FormField>

        <FormField
          label={t('admin:f43fc.createActor.displayName_label', { defaultValue: 'Display name' })}
          helper={t('admin:f43fc.createActor.displayName_helper', {
            defaultValue: '2–80 characters.',
          })}
          required
        >
          <TextInput
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={80}
            data-testid="create-actor-displayName"
            aria-label={t('admin:f43fc.createActor.displayName_label', {
              defaultValue: 'Display name',
            })}
          />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField
            label={t('admin:f43fc.createActor.country_label', {
              defaultValue: 'Country (optional)',
            })}
            helper={t('admin:f43fc.createActor.country_helper', {
              defaultValue: 'ISO code, default LB.',
            })}
          >
            <TextInput
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              maxLength={2}
              data-testid="create-actor-country"
              aria-label={t('admin:f43fc.createActor.country_label', {
                defaultValue: 'Country (optional)',
              })}
            />
          </FormField>

          <FormField
            label={t('admin:f43fc.createActor.taxId_label', { defaultValue: 'Tax ID (optional)' })}
          >
            <TextInput
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              maxLength={50}
              data-testid="create-actor-taxId"
              aria-label={t('admin:f43fc.createActor.taxId_label', {
                defaultValue: 'Tax ID (optional)',
              })}
            />
          </FormField>
        </div>

        {serverError ? (
          <div data-testid="create-actor-server-error">
            <Banner tone="danger">
              {serverError}
              {existingAccountId ? (
                <span className="ms-1 font-mono text-[12px]" data-testid="create-actor-existing-id">
                  ({existingAccountId})
                </span>
              ) : null}
            </Banner>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}

export default CreateActorDialog;
